/**
 * First-login welcome tutorial for subscribers.
 *
 * Renders a 3-step modal the first time a subscriber lands on the app.
 * Persists "seen" state in localStorage so it never reappears for the
 * same browser. Admins/creators never see it.
 *
 * Added 2026-05-17 per user request.
 */

import React, { useState } from 'react';
import './FirstLoginTutorial.css';

const STORAGE_KEY = 'oj_tutorial_seen_v1';

export function hasTutorialBeenSeen(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return true;
  }
}

export function markTutorialSeen(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {}
}

interface FirstLoginTutorialProps {
  /** Name to greet the user — first name preferred. Falls back to "there". */
  firstName?: string;
  onDismiss: () => void;
}

const STEPS = [
  {
    eyebrow: 'Welcome to OnlyJalen',
    title: '{name} — you\'re in.',
    body: 'You now have access to exclusive content, direct messaging, and 1-of-1 drops. Quick tour?',
    cta: 'Show me around',
  },
  {
    eyebrow: 'Here\'s what you can do',
    title: 'Four tabs, four ways to engage.',
    body: '',
    cards: [
      { icon: '🏠', label: 'Home',     copy: 'Daily posts + 24h stories from Jalen.' },
      { icon: '🛒', label: 'Shop',     copy: 'Make offers on 1-of-1 items. Bid above the top to win.' },
      { icon: '💬', label: 'Messages', copy: 'DM Jalen directly. Replies usually within 24h.' },
      { icon: '👤', label: 'Profile',  copy: 'Manage your subscription, addresses, and notifications.' },
    ],
    cta: 'Got it',
  },
  {
    eyebrow: 'You\'re ready',
    title: 'Tap a tab below to start.',
    body: 'Pro tip — the heart on a post adds it to your wishlist. New messages alert in the top-right bell.',
    cta: 'Let\'s go',
  },
] as const;

const FirstLoginTutorial: React.FC<FirstLoginTutorialProps> = ({ firstName, onDismiss }) => {
  const [step, setStep] = useState(0);
  const total = STEPS.length;
  const current = STEPS[step];
  const isLast = step === total - 1;

  const handleNext = () => {
    if (isLast) {
      markTutorialSeen();
      onDismiss();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleSkip = () => {
    markTutorialSeen();
    onDismiss();
  };

  const titleRendered = current.title.replace('{name}', firstName || 'Hey');

  return (
    <div className="oj-tut-overlay" role="dialog" aria-modal="true" aria-label="Welcome tutorial">
      <div className="oj-tut-card" onClick={e => e.stopPropagation()}>
        <button className="oj-tut-skip" onClick={handleSkip} type="button">Skip tour</button>

        <div className="oj-tut-progress">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`oj-tut-dot ${i === step ? 'oj-tut-dot--active' : ''} ${i < step ? 'oj-tut-dot--done' : ''}`}
            />
          ))}
        </div>

        <div className="oj-tut-body">
          <span className="oj-tut-eyebrow">{current.eyebrow}</span>
          <h2 className="oj-tut-title">{titleRendered}</h2>
          {current.body && <p className="oj-tut-text">{current.body}</p>}

          {'cards' in current && current.cards && (
            <div className="oj-tut-cards">
              {current.cards.map(c => (
                <div key={c.label} className="oj-tut-feature">
                  <span className="oj-tut-feature-icon" aria-hidden="true">{c.icon}</span>
                  <div className="oj-tut-feature-text">
                    <span className="oj-tut-feature-label">{c.label}</span>
                    <span className="oj-tut-feature-copy">{c.copy}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button className="oj-tut-cta" onClick={handleNext} type="button">
          {current.cta}
          {!isLast && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default FirstLoginTutorial;
